import { useCallback, useMemo } from "react";
import { CogIcon, LogoutIcon, PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { ICellRendererParams } from "ag-grid-community";

import { InviteMember } from "../../../InviteMember";
import { NormalizedMemberData } from "../../hooks/utils";
import {
  ENTERPRISE_IDENTITY_LEAVE_WORKSPACE_INFO,
  ENTERPRISE_IDENTITY_REMOVE_FROM_WORKSPACE_INFO,
} from "../../constants";
import { TruncatedRolesBadges } from "../TruncatedRolesBadges";

import { Button } from "@/components/ui/button";
import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { TableColumn } from "@/components/ui/TableV2";
import { Action, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { ContextMenuItems, ContextMenuVariant } from "@/components/ui/ContextMenu";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Workspace } from "@/types/workspaces";
import { WorkspaceInvite } from "@/types/workspace-invite";
import { useDeleteWorkspaceInvite } from "@/queries/workspace";
import { useConfirm } from "@/context/confirm-context";
import { ConfirmationModalVariant } from "@/components/ConfirmationModal";

export interface WorkspaceTableRow {
  id: string;
  user_status: "active" | "pending_invite";
  workspace: Workspace;
  inviteId?: number;
  inviteExpiresAt?: string;
}

interface UseWorkspacesTableParams {
  orgMember: NormalizedMemberData | null;
  memberWorkspaces: Workspace[];
  pendingInvites: WorkspaceInvite[];
  isOrgOwner: boolean;
  isRBACEnabled: boolean;
  isViewingSelf: boolean;
  isEnterpriseIdentityEnabled: boolean;
  handleRemoveFromWorkspace: (workspace?: Workspace) => Promise<void>;
  handleManageRoles: (workspace: Workspace) => void;
  isInviteDialogOpen: boolean;
  setIsInviteDialogOpen: (open: boolean) => void;
  organizationId: number;
  currentUserId?: number;
  onInviteDelete?: () => void;
  onInviteCreate?: () => void;
}

export const useWorkspacesTable = ({
  orgMember,
  memberWorkspaces,
  pendingInvites,
  isOrgOwner,
  isRBACEnabled,
  isViewingSelf,
  isEnterpriseIdentityEnabled,
  handleRemoveFromWorkspace,
  handleManageRoles,
  isInviteDialogOpen,
  setIsInviteDialogOpen,
  organizationId,
  currentUserId,
  onInviteDelete,
  onInviteCreate,
}: UseWorkspacesTableParams) => {
  const isCurrentUserAdminInWorkspace = useCallback(
    (workspace: Workspace) => !!currentUserId && workspace.admin_user_ids?.includes(currentUserId),
    [currentUserId],
  );

  const deleteWorkspaceInvite = useDeleteWorkspaceInvite({
    message: "Invite cancelled successfully",
  });
  const { confirm } = useConfirm();

  const pendingInviteRows: WorkspaceTableRow[] = useMemo(() => {
    if (!orgMember || !("user_id" in orgMember)) {
      return [];
    }
    return pendingInvites
      .filter(invite => invite.workspace)
      .map(invite => ({
        id: `pending-${invite.id}`,
        user_status: "pending_invite" as const,
        workspace: invite.workspace,
        inviteId: invite.id,
        inviteExpiresAt: invite.expires_at,
      }));
  }, [orgMember, pendingInvites]);

  const activeWorkspaceRows: WorkspaceTableRow[] = useMemo(
    () =>
      memberWorkspaces.map(workspace => ({
        id: `active-${workspace.id}`,
        user_status: "active" as const,
        workspace,
      })),
    [memberWorkspaces],
  );

  const workspaceTableData: WorkspaceTableRow[] = useMemo(
    () => [...activeWorkspaceRows, ...pendingInviteRows],
    [activeWorkspaceRows, pendingInviteRows],
  );

  const handleCancelInvite = useCallback(
    async (row?: WorkspaceTableRow) => {
      if (!row || row.user_status !== "pending_invite" || !row.inviteId) {
        return;
      }
      const isConfirmed = await confirm?.({
        message: `Are you sure you want to cancel this invite to ${row.workspace.name}?`,
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructiveOutline",
        exitButtonVariant: "outline",
      });
      if (isConfirmed) {
        await deleteWorkspaceInvite.mutateAsync(row.inviteId);
        onInviteDelete?.();
      }
    },
    [confirm, deleteWorkspaceInvite, onInviteDelete],
  );

  const workspacesColumns: TableColumn<WorkspaceTableRow>[] = useMemo(
    () => [
      {
        id: 1,
        name: "name",
        field: "workspace.name",
        header: "Workspace",
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<WorkspaceTableRow>) => (
          <span className="text-sm text-gray-900">{params.data?.workspace.name}</span>
        ),
      },
      {
        id: 2,
        name: "workspaceRole",
        header: "Workspace Role",
        cellRenderer: (params: ICellRendererParams<WorkspaceTableRow>) => {
          const row = params.data;
          const workspace = row?.workspace;
          if (!workspace || !orgMember || !("user_id" in orgMember)) {
            return "-";
          }

          if (row.user_status === "pending_invite") {
            const isExpired = !!row.inviteExpiresAt && new Date(row.inviteExpiresAt) < new Date();
            return (
              <Badge variant={isExpired ? BadgeVariant.RED : BadgeVariant.ORANGE}>
                {isExpired ? "Expired Invite" : "Pending Invite"}
              </Badge>
            );
          }

          const orgMemberWorkspace = orgMember.workspaces?.find(
            (ws: { name: string }) => ws.name === workspace.name,
          );
          const isAdmin =
            (orgMemberWorkspace &&
              "is_admin" in orgMemberWorkspace &&
              orgMemberWorkspace.is_admin) ??
            workspace.is_admin ??
            workspace.admin_user_ids?.includes(orgMember.user.id) ??
            false;
          const rawRoles =
            orgMemberWorkspace && "roles" in orgMemberWorkspace
              ? orgMemberWorkspace.roles
              : undefined;
          const additionalRoles = Array.isArray(rawRoles)
            ? rawRoles.filter((role: string) => role.toLowerCase() !== "admin")
            : [];
          return (
            <div className="flex w-full items-center gap-1 overflow-hidden">
              <Badge variant={isAdmin ? BadgeVariant.BLUE : BadgeVariant.GRAY}>
                {isAdmin ? "Admin" : "Member"}
              </Badge>
              {isRBACEnabled && <TruncatedRolesBadges roles={additionalRoles} />}
            </div>
          );
        },
      },
    ],
    [orgMember, isRBACEnabled],
  );

  const removeFromWorkspaceInfoTooltip = useMemo(() => {
    if (!isEnterpriseIdentityEnabled) {
      return undefined;
    }
    return isViewingSelf
      ? ENTERPRISE_IDENTITY_LEAVE_WORKSPACE_INFO
      : ENTERPRISE_IDENTITY_REMOVE_FROM_WORKSPACE_INFO;
  }, [isEnterpriseIdentityEnabled, isViewingSelf]);

  const workspacesActions: Action<WorkspaceTableRow>[] = useMemo(() => {
    const actionsList: Action<WorkspaceTableRow>[] = [];

    if (isRBACEnabled) {
      actionsList.push({
        id: "manage-roles",
        label: "Manage Roles",
        icon: CogIcon,
        showIf: row => row?.user_status === "active",
        getDisabled: row =>
          !isOrgOwner && (!row?.workspace || !isCurrentUserAdminInWorkspace(row.workspace)),
        tooltipContent: "Only organization owners or workspace admins can manage roles",
        onClick: (row?: WorkspaceTableRow) => {
          if (row?.workspace) {
            handleManageRoles(row.workspace);
          }
        },
      });
    }

    actionsList.push({
      id: "leave-or-remove",
      label: isViewingSelf ? "Leave workspace" : "Remove from workspace",
      icon: isViewingSelf ? LogoutIcon : TrashIcon,
      variant: DropdownMenuVariant.DESTRUCTIVE,
      showIf: row => row?.user_status === "active",
      getDisabled: row => row?.user_status === "active" && !isOrgOwner && !isViewingSelf,
      tooltipContent: "Only organization owners can remove members from workspaces",
      infoTooltipContent: removeFromWorkspaceInfoTooltip,
      onClick: (row?: WorkspaceTableRow) => handleRemoveFromWorkspace(row?.workspace),
    });

    actionsList.push({
      id: "cancel-invite",
      label: "Cancel invite",
      icon: TrashIcon,
      variant: DropdownMenuVariant.DESTRUCTIVE,
      showIf: row => row?.user_status === "pending_invite",
      getDisabled: () => !isOrgOwner,
      tooltipContent: "Only organization owners can cancel invites",
      onClick: row => handleCancelInvite(row),
    });

    return actionsList;
  }, [
    isOrgOwner,
    isRBACEnabled,
    isViewingSelf,
    removeFromWorkspaceInfoTooltip,
    isCurrentUserAdminInWorkspace,
    handleRemoveFromWorkspace,
    handleManageRoles,
    handleCancelInvite,
  ]);

  const workspacesContextMenuItems: ContextMenuItems<WorkspaceTableRow>[] = useMemo(() => {
    const items: ContextMenuItems<WorkspaceTableRow>[] = [];

    if (isRBACEnabled) {
      items.push({
        id: "manage-roles",
        label: "Manage Roles",
        icon: CogIcon,
        showIf: row => row.user_status === "active",
        getDisabled: row => !isOrgOwner && !isCurrentUserAdminInWorkspace(row.workspace),
        tooltipContent: "Only organization owners or workspace admins can manage roles",
        onClick: (row: WorkspaceTableRow) => {
          handleManageRoles(row.workspace);
        },
      });
    }

    items.push({
      id: "remove-from-workspace",
      label: isViewingSelf ? "Leave workspace" : "Remove from workspace",
      icon: isViewingSelf ? LogoutIcon : TrashIcon,
      variant: ContextMenuVariant.DESTRUCTIVE,
      showIf: row => row.user_status === "active",
      getDisabled: () => !isOrgOwner && !isViewingSelf,
      tooltipContent: "Only organization owners can remove members from workspaces",
      infoTooltipContent: removeFromWorkspaceInfoTooltip,
      onClick: (row: WorkspaceTableRow) => {
        handleRemoveFromWorkspace(row.workspace);
      },
    });

    items.push({
      id: "cancel-invite",
      label: "Cancel invite",
      icon: TrashIcon,
      variant: ContextMenuVariant.DESTRUCTIVE,
      showIf: row => row.user_status === "pending_invite",
      getDisabled: () => !isOrgOwner,
      tooltipContent: "Only organization owners can cancel invites",
      onClick: (row: WorkspaceTableRow) => {
        handleCancelInvite(row);
      },
    });

    return items;
  }, [
    isOrgOwner,
    isRBACEnabled,
    isViewingSelf,
    removeFromWorkspaceInfoTooltip,
    isCurrentUserAdminInWorkspace,
    handleRemoveFromWorkspace,
    handleManageRoles,
    handleCancelInvite,
  ]);

  const inviteToWorkspaceAction = useMemo(() => {
    if (!isOrgOwner) {
      return undefined;
    }
    if (isViewingSelf) {
      return (
        <BasicTooltip content="You cannot invite yourself">
          <span className="flex">
            <Button variant="outline" disabled>
              <PlusIcon className="mr-2 h-4 w-4" /> Invite to Workspace
            </Button>
          </span>
        </BasicTooltip>
      );
    }
    return (
      <InviteMember
        open={isInviteDialogOpen}
        setOpen={setIsInviteDialogOpen}
        hasTrigger={false}
        organizationId={organizationId}
        email={orgMember && "user_id" in orgMember ? orgMember.user.email : undefined}
        buttonLabel="Invite to Workspace"
        excludeWorkspaceIds={workspaceTableData.map(row => row.workspace.id)}
        onInviteCreate={onInviteCreate}
      />
    );
  }, [
    isOrgOwner,
    isViewingSelf,
    isInviteDialogOpen,
    setIsInviteDialogOpen,
    organizationId,
    orgMember,
    workspaceTableData,
    onInviteCreate,
  ]);

  return {
    workspaceTableData,
    workspacesColumns,
    workspacesActions,
    workspacesContextMenuItems,
    inviteToWorkspaceAction,
  };
};
